import { create } from 'zustand'
import { onWsEvent } from './websocket'
import type { components } from '@/api/schema'

export type PlatformStatus = components['schemas']['PlatformStatus']

/**
 * The known-issue banner's live state (CLA-936).
 *
 * Two sources, deliberately ranked:
 *
 *   - `GET /settings` carries `platform_status` so a client opening MID-incident
 *     renders the banner immediately, rather than waiting for the next change.
 *   - the `platform_status_changed` WebSocket event carries every change after
 *     that, including the resolution.
 *
 * `wsStatus === undefined` means "the socket hasn't spoken yet", which is NOT
 * the same as "there is no incident" (`null`). Collapsing those two would make
 * a resolved incident indistinguishable from a fresh page load, and the banner
 * would linger. The component picks settings only while `undefined`.
 */
interface PlatformStatusState {
  /** undefined = no WS update yet; null = explicitly no incident. */
  wsStatus: PlatformStatus | null | undefined
  /**
   * Message text the clinician dismissed this session. Keyed on the TEXT, not a
   * boolean: dismissing "transcription is slow" must not also hide a later
   * "don't start a new recording". A new incident is a new message.
   */
  dismissedMessage: string | null
  setStatus: (status: PlatformStatus | null) => void
  dismiss: (message: string) => void
}

const DISMISS_KEY = 'claro:platform-status-dismissed'

// Session-scoped, not local: an incident dismissed today should not stay
// dismissed tomorrow, and sessionStorage expires with the tab by construction.
function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(DISMISS_KEY)
  } catch {
    // Private browsing / blocked storage. Failing to remember a dismissal just
    // means the banner reappears — annoying, never unsafe.
    return null
  }
}

export const usePlatformStatusStore = create<PlatformStatusState>(set => ({
  wsStatus: undefined,
  dismissedMessage: readDismissed(),

  setStatus: status =>
    set(state => ({
      wsStatus: status,
      // A change of message re-arms the banner. Clearing (null) also re-arms,
      // so a later incident with coincidentally identical text still shows.
      dismissedMessage:
        status && status.message === state.dismissedMessage ? state.dismissedMessage : null,
    })),

  dismiss: message => {
    try {
      sessionStorage.setItem(DISMISS_KEY, message)
    } catch {
      // Ignore — see readDismissed.
    }
    set({ dismissedMessage: message })
  },
}))

// Module-level subscription, mirroring stores/messaging.ts. The payload is the
// status itself or null; null is meaningful and must reach the store, which is
// why this doesn't guard on truthiness.
onWsEvent(event => {
  const e = event as { type?: string; status?: PlatformStatus | null }
  if (e.type === 'platform_status_changed') {
    usePlatformStatusStore.getState().setStatus(e.status ?? null)
  }
})
